import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronLeft, FileUp, CheckCircle2, FileText, Stamp, Signature, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import ScoreCard from '@/components/ScoreCard';
import IssueList from '@/components/IssueList';

// Helper to normalize issue objects
const normalizeIssue = (issue, idx, severity) => ({
  id: `${severity[0]}${idx}`,
  title: typeof issue === 'string' ? issue : issue?.title || 'Unknown',
  description: typeof issue === 'string' ? issue : issue?.description || '',
  recommendation: typeof issue === 'object' ? issue?.recommendation : null,
  severity
});

// Determine risk level from score
const getRiskLevel = (score) => {
  if (score >= 85) return 'low';
  if (score >= 70) return 'medium';
  return 'high';
};

const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { analysis, isRealData, isPaid } = location.state || {};
  
  // Redirect if not authorized
  useEffect(() => {
    if (!analysis || !isPaid) {
      navigate('/upload', { replace: true });
    }
  }, [analysis, isPaid, navigate]);

  // Memoize computed data
  const documentTypeInfo = useMemo(() => {
    if (!analysis) return null;
    return {
      documentType: analysis.document_type || 'Unknown Document',
      typeConfidence: analysis.type_confidence || 0,
      detectedElements: analysis.detected_elements || [],
      detectedLanguage: analysis.detected_language || 'Unknown',
      hasOfficialStamps: analysis.has_official_stamps || false,
      hasSignatures: analysis.has_signatures || false,
      issuingAuthority: analysis.issuing_authority || 'Unknown'
    };
  }, [analysis]);

  const result = useMemo(() => {
    if (!analysis) return null;
    const score = analysis.readiness_score || 0;
    return {
      documentCategory: analysis.document_type || 'Document',
      readinessScore: score,
      status: analysis.status || 'warning',
      riskLevel: getRiskLevel(score),
      criticalIssues: (analysis.critical_issues || []).map((issue, idx) => 
        normalizeIssue(issue, idx, 'critical')
      ),
      warningIssues: (analysis.warnings || []).map((issue, idx) => 
        normalizeIssue(issue, idx, 'warning')
      ),
      passedChecks: (analysis.passed_checks || []).map((check, idx) => 
        normalizeIssue(check, idx, 'passed')
      )
    };
  }, [analysis]);

  const fixInstructions = [
    'Review and address all critical issues first',
    'Work through warnings to improve your readiness score',
    'Ensure all required stamps and signatures are present',
    'If apostille is needed, contact your Secretary of State office',
    'Consider getting a sworn translation if document is not in Spanish'
  ];

  // Don't render if redirecting
  if (!analysis || !isPaid || !result) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/upload')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-20" aria-hidden="true" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2">
            Analysis Complete
          </h1>
          <p className="font-public text-lg text-slate-600 mb-8">
            Here's what we found in your document
            {isRealData && (
              <span className="ml-2 px-2 py-1 bg-verify text-verify-foreground text-xs rounded-md font-mono">
                AI ANALYSIS
              </span>
            )}
          </p>

          {/* Document Type Detection Card */}
          {documentTypeInfo && isRealData && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-trust/5 to-verify/5 border border-trust/20 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start gap-4">
                <div className="bg-trust/10 p-3 rounded-lg flex-shrink-0">
                  <FileText className="w-8 h-8 text-trust" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h2 className="font-outfit font-bold text-xl text-slate-900">
                      {documentTypeInfo.documentType}
                    </h2>
                    <span className="px-2 py-1 bg-trust/10 text-trust text-xs rounded-md font-mono">
                      {documentTypeInfo.typeConfidence}% confidence
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{documentTypeInfo.detectedLanguage}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Stamp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        {documentTypeInfo.hasOfficialStamps ? 'Stamps detected' : 'No stamps'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Signature className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        {documentTypeInfo.hasSignatures ? 'Signatures present' : 'No signatures'}
                      </span>
                    </div>
                    
                    {documentTypeInfo.issuingAuthority !== 'Unknown' && (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-600 truncate">
                          {documentTypeInfo.issuingAuthority}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {documentTypeInfo.detectedElements.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-500 mb-2">Key elements identified:</p>
                      <div className="flex flex-wrap gap-2">
                        {documentTypeInfo.detectedElements.slice(0, 5).map((element, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                          >
                            {element}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Score Card */}
          <div className="mb-8">
            <ScoreCard
              score={result.readinessScore}
              status={result.status}
              documentCategory={result.documentCategory}
              riskLevel={result.riskLevel}
            />
          </div>

          {/* Issues Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <IssueList issues={result.criticalIssues} type="critical" />
            <IssueList issues={result.warningIssues} type="warning" />
          </div>

          {/* Passed Checks */}
          <div className="mb-8">
            <IssueList issues={result.passedChecks} type="passed" />
          </div>

          {/* Fix Instructions */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8 shadow-sm">
            <h3 className="font-outfit font-bold text-xl text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-trust" />
              Recommended Next Steps
            </h3>
            <ol className="space-y-3 list-decimal list-inside">
              {fixInstructions.map((instruction, idx) => (
                <li key={idx} className="font-public text-slate-700">
                  {instruction}
                </li>
              ))}
            </ol>
          </div>

          {/* Check Another Document */}
          <div className="text-center">
            <button
              onClick={() => navigate('/upload')}
              className="bg-white border-2 border-trust text-trust px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust hover:text-trust-foreground active:scale-95 transition-all shadow-sm inline-flex items-center gap-2"
              data-testid="check-another-button"
            >
              <FileUp className="w-5 h-5" />
              Check Another Document
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ResultsPage;
